import React, { useMemo } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import TrendChart from "./primitives/TrendChart";

/**
 * TrendCard — 7-day Booking Status trend chart.
 *
 * Wraps the TrendChart primitive (built in step 2) with a header, legend
 * chips, and a dynamic week-over-week confirmed-seats summary derived
 * from the first vs. last data point.
 *
 * Props:
 *   data    — [{day, confirmed, rac, waiting}, ...] (7 entries)
 *   loading — bool; renders a skeleton block
 */

const computeSubtitle = (data) => {
  if (!data || data.length < 2) return "No data yet";
  const first = data[0]?.confirmed ?? 0;
  const last = data[data.length - 1]?.confirmed ?? 0;
  if (first === 0 && last === 0) return "No confirmed seats this week";
  if (first === 0) {
    return (
      <>
        Confirmed seats <span className="text-emerald-400 font-medium">+{last}</span> this week
      </>
    );
  }
  const diff = last - first;
  const pct = Math.round((diff / first) * 100);
  if (diff === 0) {
    return (
      <>
        Confirmed seats <span className="text-zinc-300 font-medium">flat</span> from last week
      </>
    );
  }
  const sign = diff > 0 ? "+" : "";
  const toneCls = diff > 0 ? "text-emerald-400" : "text-rose-400";
  const directionWord = diff > 0 ? "up" : "down";
  return (
    <>
      Confirmed seats {directionWord}{" "}
      <span className={`${toneCls} font-medium`}>
        {sign}
        {pct}%
      </span>{" "}
      from last week
    </>
  );
};

const TrendCard = ({ data, loading = false }) => {
  const subtitle = useMemo(() => computeSubtitle(data), [data]);

  if (loading) {
    return (
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4">
        <Skeleton className="h-4 w-44 mb-2" />
        <Skeleton className="h-3 w-64 mb-4" />
        <Skeleton className="h-[170px] w-full" />
      </div>
    );
  }

  return (
    <div
      className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4"
      data-testid="trend-card"
    >
      <div className="flex items-start justify-between mb-2 gap-2 flex-wrap">
        <div className="min-w-0">
          <h3 className="text-[13px] font-semibold text-zinc-100 mb-0.5 font-heading">
            Booking Status — 7d Trend
          </h3>
          <p className="text-[11px] text-zinc-500">{subtitle}</p>
        </div>
        <div className="flex items-center gap-3 text-[10.5px] font-mono shrink-0">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
            CNF
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-amber-400" />
            RAC
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-rose-400" />
            WL
          </span>
        </div>
      </div>
      <div className="-ml-1">
        <TrendChart data={data} height={170} />
      </div>
    </div>
  );
};

export default TrendCard;
